import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { showDeleteConfirm, showSuccess, showError } from '../../utils/swal';
import { Plus, UserCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';

const UserList = () => {
  const { user } = useAuth();
  const { canView, canCreate, canEdit, canDelete } = usePermission();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!canView('users')) {
      navigate('/');
      return;
    }

    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      // API hazır değilse boş liste göster
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await showDeleteConfirm();
    if (result.isConfirmed) {
      try {
        await api.delete(`/users/${id}`);
        showSuccess('Kullanıcı başarıyla silindi');
        fetchUsers();
      } catch (error) {
        console.error('Kullanıcı silinemedi:', error);
        showError('Kullanıcı silinirken bir hata oluştu');
      }
    }
  };

  const columns = [
    {
      header: 'Ad Soyad',
      accessor: 'name',
      render: (row) => (
        <div className="flex items-center gap-2">
          <UserCheck size={16} className="text-gray-400 dark:text-gray-500" />
          <span className="font-medium text-gray-900 dark:text-white">{row.name}</span>
        </div>
      )
    },
    { header: 'E-posta', accessor: 'email' },
    {
      header: 'Departman',
      render: (row) => row.department?.name || '-'
    },
    {
      header: 'Rol',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.is_manager
          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
          }`}>
          {row.is_manager ? 'Yönetici' : 'Personel'}
        </span>
      )
    },
    {
      header: 'Durum',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'active'
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
          }`}>
          {row.status === 'active' ? 'Aktif' : 'Pasif'}
        </span>
      )
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Personeller</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Tüm personelleri görüntüleyin ve yönetin</p>
        </div>
        {canCreate('users') && (
          <Button onClick={() => navigate('/departments/users/new')}>
            <Plus size={20} />
            Yeni Personel
          </Button>
        )}
      </div>

      <Card noPadding>
        <Table
          columns={columns}
          data={users}
          onView={(row) => navigate(`/departments/users/${row.id}`)}
          onEdit={(row) => navigate(`/departments/users/${row.id}/edit`)}
          onDelete={(row) => handleDelete(row.id)}
          canEdit={() => canEdit('users')}
          canDelete={() => canDelete('users')}
        />
      </Card>
    </div>
  );
};

export default UserList;
